import { getConsumerConfig } from "../../consumers";
import { useConsumer } from "../ConsumerProvider";
import styles from "./BrandLogo.module.scss";

/**
 * Same rule as every screen's own header before VEX-7983:
 * - `partner`: the partner logo alone, as a plain <img>, whenever there is one.
 * - `idme`: otherwise the IDMe logo, now Fragno's `<fragno-logo-lockup>`.
 */
export type BrandLogoMode = "idme" | "partner";

export interface PartnerLogo {
  src: string;
  alt: string;
}

/** The one rule every screen follows: partner logo if there is one, otherwise IDMe. */
export function resolveBrandLogoMode(partnerLogo: PartnerLogo | null | undefined): BrandLogoMode {
  return partnerLogo?.src ? "partner" : "idme";
}

/**
 * Branding for the current flow (see ConsumerProvider). The partner logo is a
 * runtime one when the host passed it (Consent's `partnerLogoUrl`), otherwise
 * the bundled consumer's.
 */
export function useBrandLogo() {
  const { consumer, partnerLogo } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const partner = partnerLogo ?? consumerConfig?.logo ?? null;
  return {
    mode: resolveBrandLogoMode(partner),
    partner,
  };
}

/**
 * Whether a screen shows its "We work with IDMe" partner footer: whenever the
 * header shows the partner logo instead of IDMe, as before VEX-7983. With the
 * Fragno logo on that is decided by the shared header; otherwise by the screen's
 * own consumer logo.
 */
export function useShowPartnerFooter(legacyConsumerLogo: PartnerLogo | null | undefined): boolean {
  const { fragnoLogoEnabled } = useConsumer();
  const { mode } = useBrandLogo();
  return fragnoLogoEnabled ? mode === "partner" : Boolean(legacyConsumerLogo);
}

export interface BrandLogoProps {
  /** Accessible name for the IDMe mark (e.g. a translated "IDMe"). Defaults to "IDMe". */
  idmeLabel?: string;
  className?: string;
}

/**
 * The header logo. Rendered once, by BrandLayout; which logo shows is decided from context.
 * The IDMe logo is Fragno's `<fragno-logo-lockup>` web component, from the package
 * loaded on IDME_INIT (fragno-next with the flag on; see loadFragno). It is
 * rendered straight away and upgrades once that package has defined it.
 */
export function BrandLogo({ idmeLabel = "IDMe", className }: BrandLogoProps) {
  const { mode, partner } = useBrandLogo();

  if (mode === "partner" && partner) {
    return (
      <span className={[styles.partner, className].filter(Boolean).join(" ")} data-mode={mode}>
        <img src={partner.src} alt={partner.alt} />
      </span>
    );
  }

  return (
    <fragno-logo-lockup
      class={[styles.lockup, className].filter(Boolean).join(" ")}
      variant="brand"
      aria-label={idmeLabel}
      data-mode={mode}
    />
  );
}
